import { Check, MessageSquare, Paperclip, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";

import { Button } from "@/components/ui/button";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "~/convex/_generated/api";
import { useToast } from "@/components/ui/use-toast";

type SupportRequestType = "feature_request" | "bug" | "other";

interface AttachmentFile {
  file: File;
  storageId?: string;
}

export function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<SupportRequestType | "">("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast();
  const currentUser = useQuery(api.app.getCurrentUser, {});
  const sendSupportRequest = useMutation(
    api.exp__supportRequests.sendSupportRequest,
  );
  const generateUploadUrl = useMutation(
    api.exp__supportRequests.generateUploadUrl,
  );

  // Set default email when user data loads
  useEffect(() => {
    if (currentUser?.email && !email) {
      setEmail(currentUser.email);
    }
  }, [currentUser?.email, email]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newAttachments = files.map((file) => ({ file }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (): Promise<
    Array<{
      storageId: Id<"_storage">;
      filename: string;
      contentType: string;
      size: number;
    }>
  > => {
    const uploadedFiles = [];

    for (const attachment of attachments) {
      if (attachment.file) {
        const uploadUrl = await generateUploadUrl();
        const result = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": attachment.file.type },
          body: attachment.file,
        });

        if (!result.ok) {
          throw new Error(`Failed to upload ${attachment.file.name}`);
        }

        const { storageId } = await result.json();
        uploadedFiles.push({
          storageId,
          filename: attachment.file.name,
          contentType: attachment.file.type,
          size: attachment.file.size,
        });
      }
    }

    return uploadedFiles;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!type || !subject.trim() || !description.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload files first if any
      const uploadedFiles =
        attachments.length > 0 ? await uploadFiles() : undefined;

      // Send support request via email
      await sendSupportRequest({
        type: type as SupportRequestType,
        subject: subject.trim(),
        description: description.trim(),
        attachments: uploadedFiles,
        email: email.trim() || undefined,
      });

      setIsSubmitted(true);
      toast({
        title: "Success!",
        description:
          "Support request submitted successfully! We'll get back to you via email.",
      });

      // Reset form after a delay
      setTimeout(() => {
        setIsOpen(false);
        setIsSubmitted(false);
        setType("");
        setSubject("");
        setDescription("");
        setEmail("");
        setAttachments([]);
      }, 2000);
    } catch (error) {
      console.error("Error submitting support request:", error);
      toast({
        title: "Error",
        description: "Failed to submit support request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setType("");
    setSubject("");
    setDescription("");
    setEmail("");
    setAttachments([]);
    setIsSubmitted(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2"
      >
        <MessageSquare className="w-4 h-4" />
        Give us feedback
      </Button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          {isSubmitted ? (
            <div className="py-6">
              <div className="flex items-center gap-2 text-green-700 justify-center mb-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">Request submitted!</span>
              </div>
              <p className="text-sm text-green-600 text-center">
                We'll get back to you via email.
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Give us feedback</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="type">Request Type *</Label>
                  <Select
                    value={type}
                    onValueChange={(value) =>
                      setType(value as SupportRequestType)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select request type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feature_request">
                        Feature Request
                      </SelectItem>
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief description of your request"
                    required
                  />
                </div>

                <div>
                  <Label>Screen Recordings (most helpful)</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1"
                    >
                      <Paperclip className="w-4 h-4" />
                      Add File
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>

                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {attachments.map((attachment, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm bg-muted p-2 rounded"
                        >
                          <span className="truncate">
                            {attachment.file.name}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                            className="h-6 w-6 p-0"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please provide details about your request..."
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email">Your Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    isSubmitting ||
                    !type ||
                    !subject.trim() ||
                    !description.trim()
                  }
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  We'll get back to you via email as soon as possible
                </p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
